/**
 * GW-6534: Zustand-backed accessor for the current org id.
 *
 * The Zustand store (`useStore`) is the canonical source of truth for the
 * currently-selected organization. Non-React consumers (the BaseApiClient,
 * legacy API helpers, etc.) historically reached into
 * `localStorage['gw-current-org-id']` directly. That reach broke during the
 * post-`signIn` / pre-`fetchUserOrganizations` window, where Zustand had been
 * cleared but localStorage still held the previous user's org — causing the
 * GW-6372 cross-user contamination class of bugs.
 *
 * This module exposes `getCurrentOrgIdFromStore()` for non-React contexts.
 * For React components, prefer `useCurrentOrgId` / `useRequiredOrgId` in
 * `@/hooks/useCurrentOrgId`.
 *
 * **Implementation note (anti-cycle):** the store's slices import API
 * clients, and those API clients import this accessor — so eagerly importing
 * `@/stores/store` here would close a module-load cycle. To avoid that,
 * this module exposes a `registerOrgIdStore()` setter. The store module
 * (`@/stores/store`) calls `registerOrgIdStore(useStore)` after creation;
 * any caller of `getCurrentOrgIdFromStore()` invoked before registration
 * sees `null` and can fall back as appropriate (most callers also fall
 * back to `localStorage.getItem('gw-current-org-id')`).
 */

type StoreLike = {
  getState: () => { currentOrg?: { id?: string } | null };
};

let registeredStore: StoreLike | null = null;

/**
 * Called by `@/stores/store` after the Zustand store is created. Wires the
 * accessor to the live store. Idempotent: subsequent calls overwrite the
 * pointer (useful in tests that swap the store).
 */
export function registerOrgIdStore(store: StoreLike): void {
  registeredStore = store;
}

/**
 * Returns the current org id from the Zustand store, or null when no org is
 * selected (or the store has not yet been registered).
 *
 * Does NOT throw — callers should handle null. Most callers fall back to
 * `localStorage.getItem('gw-current-org-id')` for legacy compatibility, but
 * the store value (when present) is the authoritative source.
 *
 * Safe to call from any context (effects, callbacks, non-React modules).
 * Does NOT subscribe to updates — call this on demand at the moment you
 * need the value, not at module load.
 */
export function getCurrentOrgIdFromStore(): string | null {
  if (!registeredStore) return null;
  const state = registeredStore.getState();
  return state.currentOrg?.id ?? null;
}
